/**
 * Auth Guard Component
 * Protects routes and shows loading/error states during authentication.
 * Redirects unauthenticated users to the dashboard OAuth authorize endpoint (or, for a
 * session the cashier opened, to the cashier's /comercio, which mints a new code).
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Settings, Store, WifiOff } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { AuthError, generateOAuthState, isLeaving, storeReturnToPath } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import { BrandLoader } from '@/components/brand-loader';
import { getDashboardUrl } from '@/components/theme-provider';
import { getHomeLink } from '@/lib/home-link';
import { isAggregateScope } from '@/lib/session-scope';

interface AuthGuardProps {
    children: React.ReactNode;
    fallback?: React.ReactNode;
    onUnauthenticated?: () => void;
}

function redirectToAuthorize() {
    // Logout is already navigating away; a late 401 must not re-auth behind it.
    if (isLeaving()) return;
    // Remember where the user was headed so the post-exchange init can restore it —
    // redirect_uri only carries the origin.
    storeReturnToPath(window.location.pathname + window.location.search);
    // A cashier-opened session re-auths through the cashier: /comercio mints a fresh code and
    // comes straight back here (same tab, so the stored return path survives).
    const home = getHomeLink();
    if (home.kind === 'cashier') {
        window.location.replace(`${home.url}/comercio`);
        return;
    }
    const state = generateOAuthState();
    const params = new URLSearchParams({
        response_type: 'code',
        client_id: 'menteo-agent',
        redirect_uri: window.location.origin,
        state,
    });
    // Replace instead of push: the unauthenticated agent entry is useless in history
    // (going back to it would just restart the authorize redirect).
    window.location.replace(`${getDashboardUrl()}/oauth/authorize?${params.toString()}`);
}

/**
 * Access denied screen: shown when the SSO exchange is forbidden (403) or when the identity
 * does not do commerce at all — no brand ownership and no grant, so the backend sent
 * `scope: null` (K5). Not a WhatsApp verdict any more: a tenant with no line, or with no
 * WhatsApp channel at all, is a perfectly normal commerce tenant.
 */
function AccessDeniedScreen() {
    const { t } = useTranslation();
    const dashboardUrl = getDashboardUrl();
    const home = getHomeLink();

    return (
        <div className="h-dvh flex items-center justify-center bg-background p-4">
            <div className="w-full max-w-md">
                <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-sm motion-safe:animate-fade-up">
                    <div className="mx-auto mb-5 flex size-14 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15 dark:bg-primary/20 dark:text-foreground">
                        <Store className="size-7" />
                    </div>
                    <h2 className="text-xl font-semibold tracking-tight text-foreground mb-2">
                        {t('authGuard.accessDenied.title')}
                    </h2>
                    <p className="text-sm text-pretty text-muted-foreground mb-6">
                        {t('authGuard.accessDenied.description')}
                    </p>
                    {home.kind === 'cashier' ? (
                        <Button asChild className="w-full">
                            <a href={home.url}>{t(home.labelKey)}</a>
                        </Button>
                    ) : (
                        dashboardUrl && (
                            <Button asChild className="w-full">
                                <a href={`${dashboardUrl}/commerce/channels`}>
                                    {t('authGuard.accessDenied.enableChannel')}
                                </a>
                            </Button>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

export function AuthGuard({ children, fallback, onUnauthenticated }: AuthGuardProps) {
    const { t } = useTranslation();
    const { session, loading, ready, error } = useAuth();
    const redirectedRef = useRef(false);
    const home = getHomeLink();

    // Show loading state - block until auth initialization is fully complete
    // This ensures no API calls happen with stale cookies during SSO consume
    if (loading || !ready) {
        return (
            <div className="h-dvh flex items-center justify-center bg-background">
                <BrandLoader label={t('authGuard.authenticating')} />
            </div>
        );
    }

    // Show error state — differentiate 403 (access denied) from other errors
    if (error) {
        const isForbidden = error instanceof AuthError && error.status === 403;

        if (isForbidden) {
            return <AccessDeniedScreen />;
        }

        return (
            <div className="h-dvh flex items-center justify-center bg-background p-4">
                <div className="w-full max-w-md">
                    <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-sm motion-safe:animate-fade-up">
                        <div className="mx-auto mb-5 flex size-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 ring-1 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:ring-amber-800/60">
                            <WifiOff className="size-7" />
                        </div>
                        <h2 className="text-xl font-semibold tracking-tight text-foreground mb-2">
                            {t('authGuard.connectionError.title')}
                        </h2>
                        <p className="text-sm text-pretty text-muted-foreground mb-1">
                            {t('authGuard.connectionError.description')}
                        </p>
                        <p className="text-sm text-pretty text-muted-foreground mb-6">
                            {t('authGuard.connectionError.hint')}
                        </p>
                        <Button onClick={() => window.location.reload()} className="w-full mb-2">
                            <RefreshCw className="size-4" />
                            {t('authGuard.connectionError.reload')}
                        </Button>
                        {home.url && (
                            <Button variant="outline" asChild className="w-full">
                                <a href={home.url}>
                                    {t(
                                        home.kind === 'cashier'
                                            ? home.labelKey
                                            : 'authGuard.connectionError.backToDashboard',
                                    )}
                                </a>
                            </Button>
                        )}
                        <p className="text-xs text-muted-foreground mt-6">{t('authGuard.connectionError.support')}</p>
                    </div>
                </div>
            </div>
        );
    }

    // Not authenticated — redirect to dashboard OAuth authorize
    if (!session?.authenticated) {
        if (onUnauthenticated) {
            onUnauthenticated();
        }

        if (fallback) {
            return <>{fallback}</>;
        }

        return <RedirectToLogin redirectedRef={redirectedRef} />;
    }

    // Authenticated but the identity does not do commerce: `scope === null` is the backend's
    // only "denied" verdict (K5) — an owner identity with a brand, or a location identity with
    // a grant, always gets a scope, whatever its channels or lines are. Aggregate sessions carry
    // a scope of their own and never reach this branch.
    if (!isAggregateScope(session.scope) && session.scope === null) {
        return <AccessDeniedScreen />;
    }

    // Authenticated - render children
    return <>{children}</>;
}

function RedirectToLogin({ redirectedRef }: { redirectedRef: React.RefObject<boolean> }) {
    const { t } = useTranslation();
    // Cashier URL for a cashier-opened session, otherwise the dashboard (authorize) as before.
    const homeUrl = getHomeLink().url;

    useEffect(() => {
        if (redirectedRef.current) return;
        redirectedRef.current = true;

        if (homeUrl) {
            redirectToAuthorize();
        }
    }, [redirectedRef, homeUrl]);

    if (!homeUrl) {
        return (
            <div className="h-dvh flex items-center justify-center bg-background p-4">
                <div className="w-full max-w-md">
                    <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
                        <div className="mx-auto mb-5 flex size-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                            <Settings className="size-7" />
                        </div>
                        <h2 className="text-xl font-semibold tracking-tight text-foreground mb-2">
                            {t('authGuard.configError.title')}
                        </h2>
                        <p className="text-sm text-muted-foreground">{t('authGuard.configError.description')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-dvh flex items-center justify-center bg-background">
            <BrandLoader label={t('authGuard.redirecting')} />
        </div>
    );
}
